type VisionModule = typeof import('@mediapipe/tasks-vision');
type ImageSegmenterInstance = Awaited<ReturnType<VisionModule['ImageSegmenter']['createFromOptions']>>;


let segmenter: ImageSegmenterInstance | null = null;
let isInitializing = false;
let cachedBackground: { url: string; image: HTMLImageElement } | null = null;

export const initializeSegmenter = async () => {
  if (segmenter || isInitializing) return segmenter;
  
  isInitializing = true;
  try {
    console.log('Initializing background segmenter...');

    // Load MediaPipe directly from CDN to avoid local package sourcemap resolution issues in Vite.
    const cdnUrl = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.22-rc.20250304/vision_bundle.mjs';
    const visionModule = await import(/* @vite-ignore */ cdnUrl) as VisionModule;


    const vision = await visionModule.FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm"
    );
    
    segmenter = await visionModule.ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite",
        delegate: "GPU"
      },
      runningMode: "VIDEO",
      outputCategoryMask: true,
      outputConfidenceMasks: false
    });
    
    console.log('Background segmenter initialized successfully');
    return segmenter;
  } catch (error) {
    console.error('Failed to initialize segmenter:', error);
    throw error;
  } finally {
    isInitializing = false;
  }
};

// Preload background image
const loadBackgroundImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    // Return cached image if same URL
    if (cachedBackground?.url === url) {
      resolve(cachedBackground.image);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      cachedBackground = { url, image: img };
      resolve(img);
    };
    img.onerror = (e) => reject(new Error(`Failed to load background: ${url}`));
    img.src = url;
  });
};

export const segmentBackground = async (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  background: 'blur' | string
): Promise<void> => {
  if (!segmenter) {
    await initializeSegmenter();
  }
  
  if (!segmenter) {
    throw new Error('Segmenter not initialized');
  }

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not available');

  // Set canvas size to match video
  if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
  }

  // Preload background image if needed
  let bgImage: HTMLImageElement | null = null;
  if (background !== 'blur') {
    try {
      bgImage = await loadBackgroundImage(background);
    } catch (error) {
      console.error('Failed to load background image:', error);
      // Fall back to blur if image fails to load
      background = 'blur';
    }
  }

  // Segment the frame
  const timestamp = performance.now();
  segmenter.segmentForVideo(video, timestamp, (result) => {
    if (!result.categoryMask) return;

    // Draw original video frame
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Get frame data
    const frameData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = frameData.data;
    
    // Get mask data - selfie_segmenter returns values where:
    // HIGH values (close to 255) = person/foreground
    // LOW values (close to 0) = background
    const maskData = result.categoryMask.getAsUint8Array();
    
    if (background === 'blur') {
      // Create blurred background
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (tempCtx) {
        tempCtx.filter = 'blur(15px)';
        tempCtx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const blurredData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Blend based on mask
        for (let i = 0; i < maskData.length; i++) {
          const maskValue = maskData[i]; // 0-255 scale
          const pixelIndex = i * 4;
          
          // If mask value is LOW (background area), use blurred pixels
          if (maskValue < 128) {
            pixels[pixelIndex] = blurredData.data[pixelIndex];
            pixels[pixelIndex + 1] = blurredData.data[pixelIndex + 1];
            pixels[pixelIndex + 2] = blurredData.data[pixelIndex + 2];
          }
          // Smooth edge blending for values near threshold
          else if (maskValue < 180) {
            const alpha = (maskValue - 128) / 52;
            pixels[pixelIndex] = Math.round(pixels[pixelIndex] * alpha + blurredData.data[pixelIndex] * (1 - alpha));
            pixels[pixelIndex + 1] = Math.round(pixels[pixelIndex + 1] * alpha + blurredData.data[pixelIndex + 1] * (1 - alpha));
            pixels[pixelIndex + 2] = Math.round(pixels[pixelIndex + 2] * alpha + blurredData.data[pixelIndex + 2] * (1 - alpha));
          }
        }
      }
    } else if (bgImage) {
      // Use custom background image (already preloaded)
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (tempCtx) {
        // Draw scaled background to fit canvas
        tempCtx.drawImage(bgImage, 0, 0, canvas.width, canvas.height);
        const bgData = tempCtx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Blend based on mask
        for (let i = 0; i < maskData.length; i++) {
          const maskValue = maskData[i]; // 0-255 scale
          const pixelIndex = i * 4;
          
          // If mask value is LOW (background area), use background image
          if (maskValue < 128) {
            pixels[pixelIndex] = bgData.data[pixelIndex];
            pixels[pixelIndex + 1] = bgData.data[pixelIndex + 1];
            pixels[pixelIndex + 2] = bgData.data[pixelIndex + 2];
          }
          // Smooth edge blending for values near threshold
          else if (maskValue < 180) {
            const alpha = (maskValue - 128) / 52;
            pixels[pixelIndex] = Math.round(pixels[pixelIndex] * alpha + bgData.data[pixelIndex] * (1 - alpha));
            pixels[pixelIndex + 1] = Math.round(pixels[pixelIndex + 1] * alpha + bgData.data[pixelIndex + 1] * (1 - alpha));
            pixels[pixelIndex + 2] = Math.round(pixels[pixelIndex + 2] * alpha + bgData.data[pixelIndex + 2] * (1 - alpha));
          }
        }
      }
    }
    
    ctx.putImageData(frameData, 0, 0);
  });
};
